import { stripWordJoiners } from '@/travo/i18n/wordJoiner';
import { ROOT_URL, SITE_URL, SUPPORT_EMAIL } from '@/shared/site';

/** Stable entity ids, so engines read one Organization publishing one WebSite and two apps (APP_IDS). */
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
/** Every language the site is published in (Travo's five; MemberSheep's two are among them). */
const SITE_LANGUAGES = ['en', 'zh-Hant-TW', 'ja', 'ko', 'th'];

/**
 * Inline JSON-LD. React 19 leaves non-async scripts where they render (in <body>), which
 * search engines read fine. '<' is escaped so a value can never close the script tag, and copy's line-breaking
 * word joiners are dropped.
 */
interface JsonLdProps {
  schema: object;
}

export const JsonLd = ({ schema }: JsonLdProps) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: stripWordJoiners(JSON.stringify(schema)).replace(/</g, '\\u003c') }}
  />
);

// ─── Organization and WebSite ────────────────────────────────────────────────

/**
 * The studio, described the same on every page that carries it (the root and Travo's home), so
 * engines never see two versions of one id. App facts and store links belong to each app's entity.
 */
const STUDIO_DESCRIPTION =
  'Travo Labs is an independent studio in Taiwan making Travo, the travel tracker and trip planner for iOS and Android, and MemberSheep, the membership card wallet for iPhone.';
/** Other ways people write the studio's name. */
const STUDIO_ALIASES = ['Travolabs', 'travolabs.com'];

export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'Travo Labs',
    alternateName: STUDIO_ALIASES,
    url: ROOT_URL,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    description: STUDIO_DESCRIPTION,
    email: SUPPORT_EMAIL,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: SUPPORT_EMAIL,
      availableLanguage: ['English', 'Chinese']
    }
  };

  return <JsonLd schema={schema} />;
};

export const WebsiteSchema = () => {
  // No SearchAction: the site has no search page for it to point at.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: 'Travo Labs',
    alternateName: STUDIO_ALIASES,
    url: ROOT_URL,
    description: STUDIO_DESCRIPTION,
    inLanguage: SITE_LANGUAGES,
    publisher: { '@id': ORG_ID }
  };

  return <JsonLd schema={schema} />;
};

// ─── WebPage ─────────────────────────────────────────────────────────────────

interface WebPageSchemaProps {
  name: string;
  description: string;
  /** Absolute, localized page URL (the canonical). */
  url: string;
  /** BCP 47 tag of the page's language. */
  inLanguage: string;
  /** Id of the app the page is about (APP_IDS); omit for a page about the studio. */
  about?: string;
}

/** Ties a localized page to its URL and language, so engines don't read its copy as the English site's. */
export const WebPageSchema = ({ name, description, url, inLanguage, about }: WebPageSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    name,
    description,
    url,
    inLanguage,
    isPartOf: { '@id': WEBSITE_ID },
    ...(about ? { about: { '@id': about } } : {}),
    publisher: { '@id': ORG_ID }
  };

  return <JsonLd schema={schema} />;
};

// ─── SoftwareApplication ─────────────────────────────────────────────────────

interface SoftwareAppSchemaProps {
  /** One of APP_IDS. */
  id: string;
  name: string;
  /** Absolute URL of the app's home page on this site. */
  url: string;
  description: string;
  operatingSystem: string;
  applicationCategory: string;
  inLanguage: string[];
  featureList?: string[];
  /** Store pages; omitted before release. */
  downloadUrl?: string[];
  /** Other pages about the app (community, Product Hunt), added to its store pages in sameAs. */
  sameAs?: string[];
  image?: string;
  screenshot?: string[];
  /** Price of the download; in-app purchases go in the description. */
  offers?: { '@type': 'Offer'; price: string; priceCurrency: string };
}

/** An app Travo Labs publishes. Its full entity lives on the app's home page; other pages may repeat it in short. */
export const SoftwareAppSchema = ({
  id,
  downloadUrl = [],
  sameAs = [],
  offers = { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  ...facts
}: SoftwareAppSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': id,
    ...facts,
    ...(downloadUrl.length ? { downloadUrl } : {}),
    ...(downloadUrl.length + sameAs.length ? { sameAs: [...downloadUrl, ...sameAs] } : {}),
    offers,
    publisher: { '@id': ORG_ID }
  };

  return <JsonLd schema={schema} />;
};

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

interface BreadcrumbSchemaProps {
  /** The trail below the Travo Labs root, which every breadcrumb starts with: the app, then the page. */
  items: Array<{
    name: string;
    url: string;
  }>;
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Travo Labs', url: ROOT_URL }, ...items].map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return <JsonLd schema={schema} />;
};

// ─── FAQ ──────────────────────────────────────────────────────────────────────

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

export const FAQSchema = ({ items }: FAQSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return <JsonLd schema={schema} />;
};

// ─── HowTo ────────────────────────────────────────────────────────────────────

interface HowToStep {
  name: string;
  text: string;
  position: number;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
}

export const HowToSchema = ({ name, description, steps, totalTime = 'PT5M' }: HowToSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    totalTime,
    step: steps.map((step) => ({
      '@type': 'HowToStep',
      position: step.position,
      name: step.name,
      text: step.text
    }))
  };

  return <JsonLd schema={schema} />;
};

// ─── VideoObject ──────────────────────────────────────────────────────────────

interface VideoObjectSchemaProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  contentUrl?: string;
  duration?: string;
  embedUrl?: string;
}

export const VideoObjectSchema = ({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  contentUrl,
  duration,
  embedUrl
}: VideoObjectSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl,
    uploadDate,
    ...(contentUrl ? { contentUrl } : {}),
    ...(duration ? { duration } : {}),
    ...(embedUrl ? { embedUrl } : {})
  };

  return <JsonLd schema={schema} />;
};
